/**
 * Mist Diagnostic Model — formal, shared diagnostic schema.
 *
 * Every diagnostic produced by any stage of the Mist pipeline
 * (lex, parse, type-check, static analysis, PVI lowering, build,
 * deploy preflight, simulation, adversarial) MUST be expressed
 * as a MistDiagnostic conforming to this schema.
 *
 * Consumers: Monaco markers, Problems panel, status bar, deploy panel,
 * simulation panel, adversarial panel, graph overlay.
 */

/* ================================================================== */
/*  Enums                                                             */
/* ================================================================== */

/** Broad category of the diagnostic. */
export type MistDiagnosticCategory =
  | 'Syntax'
  | 'ParseStructure'
  | 'Type'
  | 'NameResolution'
  | 'ContractStructure'
  | 'ValueFlow'
  | 'Determinism'
  | 'Policy'
  | 'PviLowering'
  | 'UvlInvariant'
  | 'Build'
  | 'DeployPreflight'
  | 'Simulation'
  | 'Adversarial'
  | 'Internal';

/** Pipeline stage that produced the diagnostic. */
export type MistDiagnosticStage =
  | 'Lex'
  | 'Parse'
  | 'TypeCheck'
  | 'Normalize'
  | 'StaticAnalysis'
  | 'PviLowering'
  | 'BundleBuild'
  | 'DeployPreflight'
  | 'Simulation'
  | 'Adversarial'
  | 'Runtime'
  | 'Unknown';

/** Diagnostic severity. */
export type MistDiagnosticSeverity = 'Error' | 'Warning' | 'Info';

/* ================================================================== */
/*  Source range                                                      */
/* ================================================================== */

/** 1-based line/column range in source text. */
export interface MistSourceRange {
  startLine: number;
  startColumn: number;
  endLine: number;
  endColumn: number;
}

/** A secondary range related to the primary diagnostic location. */
export interface MistRelatedRange {
  range: MistSourceRange;
  message: string;
}

/* ================================================================== */
/*  Explanation (EXPLAIN mode, not suggestions)                       */
/* ================================================================== */

export interface MistDiagnosticExplanation {
  title: string;
  facts: string[];
  values?: Record<string, string | number | boolean>;
  relatedEntities?: string[];
  invariant?: string;
}

/* ================================================================== */
/*  Core diagnostic                                                    */
/* ================================================================== */

export interface MistDiagnostic {
  /** Stable, machine-readable code (e.g. MIST_P0001). */
  code: string;
  /** Broad failure category. */
  category: MistDiagnosticCategory;
  /** Pipeline stage that produced this diagnostic. */
  stage: MistDiagnosticStage;
  /** Deterministic severity. */
  severity: MistDiagnosticSeverity;
  /** Human-readable primary message. Short, factual, scannable. */
  message: string;
  /** Optional one-line summary (for compact views). */
  summary?: string;
  /** Optional longer explanation text. */
  detail?: string;
  /** Source file name (if applicable). */
  sourceFile?: string;
  /** Primary source range. */
  range?: MistSourceRange;
  /** Secondary related ranges (e.g. original declaration for duplicates). */
  relatedRanges?: MistRelatedRange[];
  /** Related symbol names. */
  relatedSymbols?: string[];
  /** Blocks any further pipeline stages. */
  blocking: boolean;
  /** Blocks deployment. */
  deployBlocking: boolean;
  /** Blocks simulation engine from running. */
  simulationBlocking: boolean;
  /** Blocks PVI graph generation. */
  graphBlocking: boolean;
  /** Structured explanation (no suggestions). */
  explanation?: MistDiagnosticExplanation;
  /** Arbitrary metadata for internal/advanced use. */
  metadata?: Record<string, unknown>;
}

/* ================================================================== */
/*  Helper: create a diagnostic with sensible defaults                */
/* ================================================================== */

export function makeDiagnostic(
  fields: Pick<MistDiagnostic, 'code' | 'category' | 'stage' | 'severity' | 'message'> &
    Partial<MistDiagnostic>,
): MistDiagnostic {
  return {
    blocking: fields.severity === 'Error',
    deployBlocking: fields.severity === 'Error',
    simulationBlocking: fields.severity === 'Error' &&
      (fields.stage === 'Lex' || fields.stage === 'Parse' || fields.stage === 'TypeCheck' || fields.stage === 'Normalize'),
    graphBlocking: fields.severity === 'Error' &&
      (fields.stage === 'Lex' || fields.stage === 'Parse' || fields.stage === 'TypeCheck'),
    ...fields,
  };
}

/* ================================================================== */
/*  Aggregate result                                                  */
/* ================================================================== */

export interface MistDiagnosticResult {
  diagnostics: MistDiagnostic[];
  /** Any error-severity diagnostic exists. */
  hasErrors: boolean;
  /** Any diagnostic blocks deployment. */
  isDeployBlocked: boolean;
  /** Any diagnostic blocks simulation. */
  isSimulationBlocked: boolean;
  /** Any diagnostic blocks PVI graph generation. */
  isGraphBlocked: boolean;
  /** Highest stage that succeeded (for phase-aware status). */
  highestFailedStage: MistDiagnosticStage | null;
}

export function aggregateResult(diagnostics: MistDiagnostic[]): MistDiagnosticResult {
  const hasErrors = diagnostics.some(d => d.severity === 'Error');
  const isDeployBlocked = diagnostics.some(d => d.deployBlocking);
  const isSimulationBlocked = diagnostics.some(d => d.simulationBlocking);
  const isGraphBlocked = diagnostics.some(d => d.graphBlocking);

  const STAGE_ORDER: MistDiagnosticStage[] = [
    'Lex', 'Parse', 'TypeCheck', 'Normalize', 'StaticAnalysis',
    'PviLowering', 'BundleBuild', 'DeployPreflight', 'Simulation', 'Adversarial', 'Runtime', 'Unknown',
  ];

  let highestFailedStage: MistDiagnosticStage | null = null;
  for (const d of diagnostics) {
    if (d.severity === 'Error') {
      const idx = STAGE_ORDER.indexOf(d.stage);
      const currentIdx = highestFailedStage ? STAGE_ORDER.indexOf(highestFailedStage) : -1;
      if (idx < currentIdx || currentIdx === -1) {
        highestFailedStage = d.stage;
      }
    }
  }

  return { diagnostics, hasErrors, isDeployBlocked, isSimulationBlocked, isGraphBlocked, highestFailedStage };
}

/* ================================================================== */
/*  Phase-aware status label                                          */
/* ================================================================== */

export type MistPhaseStatus =
  | 'Clean'
  | 'SyntaxError'
  | 'CompileError'
  | 'AnalysisError'
  | 'BuildError'
  | 'DeployBlocked'
  | 'SimulationBlocked'
  | 'WarningsOnly'
  | 'Analyzing';

const STAGE_TO_PHASE: Record<MistDiagnosticStage, MistPhaseStatus> = {
  Lex: 'SyntaxError',
  Parse: 'SyntaxError',
  TypeCheck: 'CompileError',
  Normalize: 'CompileError',
  StaticAnalysis: 'AnalysisError',
  PviLowering: 'AnalysisError',
  BundleBuild: 'BuildError',
  DeployPreflight: 'DeployBlocked',
  Simulation: 'SimulationBlocked',
  Adversarial: 'AnalysisError',
  Runtime: 'AnalysisError',
  Unknown: 'CompileError',
};

export function getPhaseStatus(result: MistDiagnosticResult): MistPhaseStatus {
  if (!result.hasErrors && result.diagnostics.length === 0) return 'Clean';
  if (!result.hasErrors) return 'WarningsOnly';
  if (result.highestFailedStage) return STAGE_TO_PHASE[result.highestFailedStage];
  return 'CompileError';
}
